import React, { useState, useEffect, useCallback } from 'react';
import Select from "react-select";
import Swal from "sweetalert2/dist/sweetalert2.js";

const TYPE_COMPAT = {
    string: ["answer_string", "answer_text", "email", "url", "answer_select", "answer_radio", "code_format", "dynamic_field"],
    number: ["answer_integer", "answer_formula", "answer_number"],
    date: ["answer_date", "answer_datetime", "answer_autdatetime"]
};

const TYPE_COLORS = {
    string: "#3B82F6",
    number: "#10B981",
    date: "#F59E0B"
};

const parseFields = (rawFields) => {
    if (!rawFields) return [];
    if (Array.isArray(rawFields)) return rawFields;
    // Convert object { key: { name, type, ... } } to array
    const fields = Object.entries(rawFields).map(([key, val]) => ({
        key,
        name: val.name || key,
        type: val.type || "string",
        required: val.required || false,
        order: val.order,
        ...val
    }));
    fields.sort((a, b) => (a.order || 0) - (b.order || 0));
    return fields;
};

const ConfigurationTaimes = ({ format }) => {
    const token = document.querySelector("[name='csrf-token']").content;

    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [apiKey, setApiKey] = useState("");
    const [showApiKey, setShowApiKey] = useState(false);
    const [schemas, setSchemas] = useState([]);
    const [selectedSchema, setSelectedSchema] = useState(null);
    const [schemaFields, setSchemaFields] = useState([]);
    const [mappings, setMappings] = useState([]);
    const [questions, setQuestions] = useState([]);
    const [integration, setIntegration] = useState(null);
    const [webhookToken, setWebhookToken] = useState(null);

    // Load existing integration on mount
    useEffect(() => {
        loadIntegration();
        loadQuestions();
    }, []);

    const loadIntegration = () => {
        fetch(`/formatos/get_taimes_integration/${format.id}`, {
            method: 'GET',
            headers: { "X-CSRF-Token": token, "Content-Type": "application/json" }
        })
        .then(res => res.json())
        .then(data => {
            if (data.taimes_integration) {
                const ti = data.taimes_integration;
                setIntegration(ti);
                setApiKey(ti.api_key || "");
                if (ti.webhook_token) setWebhookToken(ti.webhook_token);
                if (ti.schema_id) {
                    setSelectedSchema({ id: ti.schema_id, name: ti.schema_name });
                    const fm = ti.field_mapping || {};
                    if (fm.mappings) setMappings(fm.mappings);
                    if (fm.schema_cache && fm.schema_cache.fields) {
                        setSchemaFields(parseFields(fm.schema_cache.fields));
                        setStep(3);
                    } else {
                        setStep(2);
                    }
                } else {
                    setStep(1);
                }
            }
        });
    };

    const loadQuestions = () => {
        fetch(`/formatos/get_questions/${format.id}`, {
            method: 'GET',
            headers: { "X-CSRF-Token": token, "Content-Type": "application/json" }
        })
        .then(res => res.json())
        .then(data => {
            const qs = (data.register || []).map(q => ({
                label: q.question,
                value: q.id,
                type: q.question_type,
                alias: q.question_alias
            }));
            setQuestions(qs);
        });
    };

    const connectTaimes = () => {
        if (!apiKey.trim()) {
            Swal.fire({ icon: "warning", title: "Ingresa tu API Key de Taimes" });
            return;
        }
        setLoading(true);
        fetch(`/formatos/taimes_schemas?api_key=${encodeURIComponent(apiKey)}`, {
            method: 'GET',
            headers: { "X-CSRF-Token": token, "Content-Type": "application/json" }
        })
        .then(res => {
            if (!res.ok) throw new Error("Error al conectar con Taimes");
            return res.json();
        })
        .then(data => {
            const schemaList = data.schemas || data.data || data || [];
            setSchemas(Array.isArray(schemaList) ? schemaList : []);
            // Save/update integration with api_key
            saveIntegrationApiKey();
            setStep(2);
        })
        .catch(err => {
            Swal.fire({ icon: "error", title: "Error de conexión", text: err.message });
        })
        .finally(() => setLoading(false));
    };

    const saveIntegrationApiKey = () => {
        if (integration) {
            fetch(`/formatos/taimes_integrations/${integration.id}`, {
                method: 'PATCH',
                headers: { "X-CSRF-Token": token, "Content-Type": "application/json" },
                body: JSON.stringify({ api_key: apiKey })
            })
            .then(res => res.json())
            .then(data => { if (data.taimes_integration) setIntegration(data.taimes_integration); });
        } else {
            fetch('/formatos/taimes_integrations', {
                method: 'POST',
                headers: { "X-CSRF-Token": token, "Content-Type": "application/json" },
                body: JSON.stringify({ format_id: format.id, api_key: apiKey })
            })
            .then(res => res.json())
            .then(data => { if (data.taimes_integration) setIntegration(data.taimes_integration); });
        }
    };

    const selectSchema = (schema) => {
        setLoading(true);
        const schemaId = schema.id || schema.uuid;
        fetch(`/formatos/taimes_schema/${schemaId}?api_key=${encodeURIComponent(apiKey)}`, {
            method: 'GET',
            headers: { "X-CSRF-Token": token, "Content-Type": "application/json" }
        })
        .then(res => {
            if (!res.ok) throw new Error("Error al obtener schema");
            return res.json();
        })
        .then(data => {
            const rawFields = data.schema?.schema?.fields || data.schema?.fields || data.fields || {};
            setSelectedSchema({ id: schemaId, name: schema.name || schema.code });
            setSchemaFields(parseFields(rawFields));
            setMappings([]);
            setStep(3);
        })
        .catch(err => {
            Swal.fire({ icon: "error", title: "Error", text: err.message });
        })
        .finally(() => setLoading(false));
    };

    const getCompatibleFields = (questionType) => {
        // Special case: file/image questions get a virtual option for Taimes document file
        if (questionType === "answer_file") {
            return [{
                label: "📎 Archivo del documento Taimes (file_url + file_name)",
                value: "__taimes_file__",
                name: "Archivo del documento",
                type: "file"
            }];
        }
        const compatEntries = Object.entries(TYPE_COMPAT).filter(([, types]) => types.includes(questionType));
        const compatTaimesTypes = compatEntries.map(([tType]) => tType);
        return schemaFields.filter(f => compatTaimesTypes.includes(f.type || "string")).map(f => ({
            label: `${f.name || f.label} (${f.type || "string"})`,
            value: f.key || f.name,
            name: f.name || f.label,
            type: f.type || "string"
        }));
    };

    const getMappingForQuestion = (questionId) => {
        return mappings.find(m => m.question_id === questionId);
    };

    const updateMapping = (question, selectedField) => {
        let newMappings = mappings.filter(m => m.question_id !== question.value);
        if (selectedField) {
            newMappings.push({
                taimes_field_key: selectedField.value,
                taimes_field_name: selectedField.name,
                taimes_type: selectedField.type || "string",
                question_id: question.value,
                question_alias: question.alias || ""
            });
        }
        setMappings(newMappings);
    };

    const autoMap = () => {
        const newMappings = [];
        questions.forEach(q => {
            const qName = (q.label || "").toLowerCase().replace(/[_\s-]/g, "");
            const qAlias = (q.alias || "").toLowerCase().replace(/[_\s-]/g, "");
            const compatFields = getCompatibleFields(q.type);

            const match = compatFields.find(f => {
                const fName = (f.name || "").toLowerCase().replace(/[_\s-]/g, "");
                const fKey = (f.value || "").toLowerCase().replace(/[_\s-]/g, "");
                return qName.includes(fName) || fName.includes(qName) ||
                       qAlias.includes(fName) || fName.includes(qAlias) ||
                       qName.includes(fKey) || fKey.includes(qName);
            });

            if (match) {
                newMappings.push({
                    taimes_field_key: match.value,
                    taimes_field_name: match.name,
                    taimes_type: match.type || "string",
                    question_id: q.value,
                    question_alias: q.alias || ""
                });
            }
        });
        setMappings(newMappings);
        Swal.fire({
            icon: "info",
            title: "Auto-mapeo completado",
            text: `${newMappings.length} de ${questions.length} preguntas mapeadas automáticamente.`,
            timer: 2500,
            showConfirmButton: false
        });
    };

    const saveConfiguration = () => {
        if (!integration) return;
        setLoading(true);
        const payload = {
            schema_id: selectedSchema.id,
            schema_name: selectedSchema.name,
            field_mapping: {
                mappings: mappings,
                schema_cache: { fields: schemaFields }
            }
        };
        fetch(`/formatos/taimes_integrations/${integration.id}`, {
            method: 'PATCH',
            headers: { "X-CSRF-Token": token, "Content-Type": "application/json" },
            body: JSON.stringify(payload)
        })
        .then(res => res.json())
        .then(data => {
            if (data.type === "success") {
                setIntegration(data.taimes_integration);
                if (data.taimes_integration.webhook_token) setWebhookToken(data.taimes_integration.webhook_token);
                Swal.fire({ icon: "success", title: "Configuración guardada", timer: 2000, showConfirmButton: false });
            } else {
                Swal.fire({ icon: "error", title: "Error al guardar", text: (data.error || []).join(", ") });
            }
        })
        .catch(() => Swal.fire({ icon: "error", title: "Error al guardar" }))
        .finally(() => setLoading(false));
    };

    const deleteIntegration = () => {
        if (!integration || !integration.id) {
            Swal.fire({ icon: "warning", title: "No hay integración para eliminar" });
            return;
        }
        Swal.fire({
            title: "¿Eliminar integración?",
            text: "Se eliminará la conexión con Taimes para este formato.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            confirmButtonText: "Eliminar",
            cancelButtonText: "Cancelar"
        }).then(result => {
            if (result.value) {
                fetch(`/formatos/taimes_integrations/${integration.id}`, {
                    method: 'DELETE',
                    headers: { "X-CSRF-Token": token, "Content-Type": "application/json" }
                })
                .then(res => res.json())
                .then(() => {
                    setIntegration(null);
                    setApiKey("");
                    setSchemas([]);
                    setSelectedSchema(null);
                    setSchemaFields([]);
                    setMappings([]);
                    setStep(1);
                    Swal.fire({ icon: "success", title: "Integración eliminada", timer: 1500, showConfirmButton: false });
                });
            }
        });
    };

    const mappedCount = mappings.length;
    const totalFields = schemaFields.length;

    // ─── RENDER ────────────────────────────────────────────────

    return (
        <div className="row p-3">
            <div className="col-md-12">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="ui-index-configurationFormat-subtitle-tab mb-0">
                        <i className="fas fa-plug mr-2"></i>Integración Taimes
                    </h5>
                    {integration && (
                        <button className="btn btn-outline-danger btn-sm" onClick={deleteIntegration}>
                            <i className="fas fa-trash mr-1"></i>Eliminar
                        </button>
                    )}
                </div>

                {/* Step Indicator */}
                <div className="taimes-step-indicator mb-4">
                    <div className={`taimes-step-circle ${step >= 1 ? "active" : ""}`}>1</div>
                    <div className={`taimes-step-line ${step >= 2 ? "active" : ""}`}></div>
                    <div className={`taimes-step-circle ${step >= 2 ? "active" : ""}`}>2</div>
                    <div className={`taimes-step-line ${step >= 3 ? "active" : ""}`}></div>
                    <div className={`taimes-step-circle ${step >= 3 ? "active" : ""}`}>3</div>
                </div>
                <div className="taimes-step-labels mb-4">
                    <span className={step >= 1 ? "active" : ""}>Conectar</span>
                    <span className={step >= 2 ? "active" : ""}>Schema</span>
                    <span className={step >= 3 ? "active" : ""}>Mapping</span>
                </div>

                {/* STEP 1 — Connect */}
                {step === 1 && (
                    <div className="card">
                        <div className="card-header" style={{ backgroundColor: '#f8f9fa' }}>
                            <strong><i className="fas fa-key mr-2"></i>Conectar con Taimes</strong>
                        </div>
                        <div className="card-body">
                            <p className="text-muted mb-3" style={{ fontSize: '0.9em' }}>
                                Ingresa tu API Key de Taimes para conectar y acceder a tus schemas.
                            </p>
                            <label className="mb-1"><strong>API Key</strong></label>
                            <div className="input-group mb-3">
                                <input
                                    type={showApiKey ? "text" : "password"}
                                    className="form-control"
                                    value={apiKey}
                                    onChange={e => setApiKey(e.target.value)}
                                    placeholder="Ingresa tu API Key de Taimes"
                                    style={{ fontFamily: 'monospace', fontSize: '0.85em' }}
                                />
                                <div className="input-group-append">
                                    <button
                                        className="btn btn-outline-secondary"
                                        type="button"
                                        onClick={() => setShowApiKey(!showApiKey)}
                                    >
                                        <i className={`fas ${showApiKey ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                                    </button>
                                </div>
                            </div>
                            <button
                                className="btn btn-primary"
                                onClick={connectTaimes}
                                disabled={loading}
                            >
                                {loading ? (
                                    <><i className="fas fa-spinner fa-spin mr-1"></i>Conectando...</>
                                ) : (
                                    <><i className="fas fa-plug mr-1"></i>Conectar</>
                                )}
                            </button>
                        </div>
                    </div>
                )}

                {/* STEP 2 — Select Schema */}
                {step === 2 && (
                    <div>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <span className="text-muted" style={{ fontSize: '0.9em' }}>
                                {schemas.length} schema(s) disponibles
                            </span>
                            <button className="btn btn-outline-secondary btn-sm" onClick={() => setStep(1)}>
                                <i className="fas fa-arrow-left mr-1"></i>Volver
                            </button>
                        </div>
                        <div className="row">
                            {schemas.map((schema, idx) => {
                                const schemaId = schema.id || schema.uuid;
                                const isSelected = selectedSchema && selectedSchema.id === schemaId;
                                return (
                                    <div className="col-md-6 col-lg-4 mb-3" key={idx}>
                                        <div
                                            className={`card taimes-schema-card ${isSelected ? "selected" : ""}`}
                                            onClick={() => selectSchema(schema)}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <div className="card-body p-3">
                                                <div className="d-flex justify-content-between align-items-start">
                                                    <h6 className="mb-1" style={{ fontWeight: 600 }}>
                                                        {schema.name || schema.code}
                                                    </h6>
                                                    {schema.field_count != null && (
                                                        <span className="badge badge-secondary">
                                                            {schema.field_count} campos
                                                        </span>
                                                    )}
                                                </div>
                                                {schema.code && (
                                                    <small className="text-muted d-block mb-1" style={{ fontFamily: 'monospace' }}>
                                                        {schema.code}
                                                    </small>
                                                )}
                                                {schema.description && (
                                                    <small className="text-muted">{schema.description}</small>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                            {schemas.length === 0 && !loading && (
                                <div className="col-12 text-center text-muted py-4">
                                    No se encontraron schemas.
                                </div>
                            )}
                        </div>
                        {loading && (
                            <div className="text-center py-3">
                                <i className="fas fa-spinner fa-spin fa-2x text-primary"></i>
                            </div>
                        )}
                    </div>
                )}

                {/* STEP 3 — Field Mapping */}
                {step === 3 && (
                    <div>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <div>
                                <h6 className="mb-0" style={{ fontWeight: 600 }}>
                                    {selectedSchema?.name}
                                </h6>
                                <small className="text-muted">
                                    {mappedCount} de {questions.length} preguntas mapeadas
                                </small>
                            </div>
                            <div>
                                <button className="btn btn-outline-secondary btn-sm mr-2" onClick={() => { setStep(2); connectTaimes(); }}>
                                    <i className="fas fa-arrow-left mr-1"></i>Schemas
                                </button>
                                <button className="btn btn-outline-info btn-sm mr-2" onClick={autoMap}>
                                    <i className="fas fa-magic mr-1"></i>Auto-mapear
                                </button>
                                <button
                                    className="btn btn-primary btn-sm"
                                    onClick={saveConfiguration}
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <><i className="fas fa-spinner fa-spin mr-1"></i>Guardando...</>
                                    ) : (
                                        <><i className="fas fa-save mr-1"></i>Guardar configuración</>
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Stats bar */}
                        <div className="taimes-stats-bar mb-3">
                            <div
                                className="taimes-stats-bar-fill"
                                style={{ width: questions.length > 0 ? `${(mappedCount / questions.length) * 100}%` : '0%' }}
                            ></div>
                        </div>

                        {/* Webhook URL */}
                        {webhookToken && (
                            <div className="card mb-3">
                                <div className="card-body py-2 px-3">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <small className="text-muted d-block" style={{ fontWeight: 600 }}>
                                                <i className="fas fa-satellite-dish mr-1"></i>Webhook URL
                                            </small>
                                            <code style={{ fontSize: '0.8em', wordBreak: 'break-all' }}>
                                                {`${window.location.origin}/webhooks/taimes/${webhookToken}`}
                                            </code>
                                        </div>
                                        <button
                                            className="btn btn-outline-secondary btn-sm ml-2"
                                            onClick={() => {
                                                navigator.clipboard.writeText(`${window.location.origin}/webhooks/taimes/${webhookToken}`);
                                                Swal.fire({ icon: "success", title: "URL copiada", timer: 1200, showConfirmButton: false });
                                            }}
                                        >
                                            <i className="fas fa-copy"></i>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Mapping table */}
                        <div className="card">
                            <div className="card-body p-0">
                                <table className="table table-sm mb-0">
                                    <thead>
                                        <tr style={{ backgroundColor: '#f8f9fa' }}>
                                            <th style={{ width: '40%' }}>Pregunta Beewo</th>
                                            <th style={{ width: '10%', textAlign: 'center' }}></th>
                                            <th style={{ width: '50%' }}>Campo Taimes</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {questions.map((q, idx) => {
                                            const mapping = getMappingForQuestion(q.value);
                                            const isMapped = !!mapping;
                                            const compatFields = getCompatibleFields(q.type);
                                            const selectedValue = isMapped
                                                ? compatFields.find(f => f.value === mapping.taimes_field_key) || null
                                                : null;

                                            return (
                                                <tr key={idx} className="taimes-mapping-row">
                                                    <td className="align-middle">
                                                        <div className="d-flex align-items-center">
                                                            <span className="mr-2" style={{ fontWeight: 500 }}>
                                                                {q.label}
                                                            </span>
                                                            <span
                                                                className="badge badge-pill"
                                                                style={{
                                                                    backgroundColor: '#6B7280',
                                                                    color: '#fff',
                                                                    fontSize: '0.7em'
                                                                }}
                                                            >
                                                                {q.type}
                                                            </span>
                                                        </div>
                                                        {q.alias && (
                                                            <small className="text-muted" style={{ fontFamily: 'monospace', fontSize: '0.75em' }}>
                                                                {q.alias}
                                                            </small>
                                                        )}
                                                    </td>
                                                    <td className="align-middle text-center">
                                                        <i
                                                            className="fas fa-arrow-left"
                                                            style={{ color: isMapped ? '#10B981' : '#D1D5DB', fontSize: '1.1em' }}
                                                        ></i>
                                                    </td>
                                                    <td className="align-middle">
                                                        <Select
                                                            options={compatFields}
                                                            value={selectedValue}
                                                            onChange={(opt) => updateMapping(q, opt)}
                                                            isClearable
                                                            placeholder="Seleccionar campo Taimes..."
                                                            noOptionsMessage={() => "Sin campos compatibles"}
                                                            styles={{
                                                                control: (base) => ({ ...base, minHeight: 34, fontSize: '0.85em' }),
                                                                option: (base) => ({ ...base, fontSize: '0.85em' })
                                                            }}
                                                        />
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                        {questions.length === 0 && (
                                            <tr>
                                                <td colSpan="3" className="text-center text-muted py-3">
                                                    No se encontraron preguntas en este formato.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ConfigurationTaimes;
